import { useEffect, useRef } from 'react';
import type { FlowTypes } from 'store/constants';
import { useAppSelector } from 'store/hooks';
import { appChannelSelector, brandSelector, clickstreamIdSelector, langSelector } from 'store/slices/global';
import { useGetSessionId } from 'utils/session';

import { useGetClickStreamCashierContext } from './useGetClickStreamCashierContext';
import { getEventAnalytic } from './Сlickstream';

interface UseClickstreamInitProps {
  flowType: FlowTypes;
  merchantUrl: string;
  authToken: string;
  merchantApiKey: string;
  platform?: string;
  isLoading: boolean;
  onReady?: () => void;
}

export const useClickstreamInit = ({
  flowType,
  merchantUrl,
  authToken,
  merchantApiKey,
  platform,
  isLoading,
  onReady,
}: UseClickstreamInitProps) => {
  const clickstream = getEventAnalytic();
  const appChannel = useAppSelector(appChannelSelector);
  const brand = useAppSelector(brandSelector);
  const language = useAppSelector(langSelector);
  const clickstreamId = useAppSelector(clickstreamIdSelector);
  const sessionId = useGetSessionId();
  const cashierContext = useGetClickStreamCashierContext(flowType);
  const onReadyCalledRef = useRef(false);

  useEffect(() => {
    if (isLoading) return;
    if (!sessionId) return;
    if (onReadyCalledRef.current) return;

    if (clickstream.isInitialized()) {
      onReadyCalledRef.current = true;
      onReady?.();
      return;
    }
    if (!clickstream.isFailed() && !clickstream.isLoading()) {
      clickstream
        .initClickstream({
          appChannel,
          platform,
          brand,
          language,
          merchantUrl: merchantUrl || '',
          clickstreamId,
          sessionId,
          authToken,
          merchantApiKey,
          cashierContext,
        })
        .then(() => {
          if (!onReadyCalledRef.current) {
            onReadyCalledRef.current = true;
            onReady?.();
          }
        });
    }
  }, [
    isLoading,
    appChannel,
    platform,
    brand,
    language,
    merchantUrl,
    clickstreamId,
    sessionId,
    authToken,
    cashierContext,
  ]);
};
